import React, { useState, useMemo } from 'react';
import { formatPercentage } from './utils';
import SectorHistoryChart from './SectorHistoryChart';
import SectorRotationMap from './SectorRotationMap';
import AssetBucketsView from './AssetBucketsView';
import '../styles/MarketHeatmap.css';

const TIMEFRAMES = [
  { key: 'daily', label: 'Today' },
  { key: 'weekly', label: 'Week' },
  { key: 'monthly', label: 'Month' },
  { key: 'quarterly', label: 'Quarter' },
];

const VIEW_MODES = [
  { key: 'sectors', label: 'Sectors' },
  { key: 'business_models', label: 'Business Models' },
  // Compounded price move per sector over time (issue #278). Has its own
  // window pills, so the heatmap's timeframe controls hide in this mode. The
  // sentiment series lives in the Fear & Greed section.
  { key: 'history', label: 'Over time' },
  // Which sectors lead and whether the lead is growing (issue #365). Descriptive only.
  { key: 'rotation', label: 'Rotation' },
  // Asset-class crowding with the one validated give-back read (issue #369).
  { key: 'buckets', label: 'Asset classes' },
];

// Views that bring their own controls and replace the heatmap grid.
const CHART_VIEWS = {
  history: <SectorHistoryChart metric="performance" embedded />,
  rotation: <SectorRotationMap />,
  buckets: <AssetBucketsView />,
};

function getMomentumColor(momentum) {
  if (momentum == null) return '#d1d5db';
  const clamped = Math.max(-5, Math.min(5, momentum));
  const normalized = (clamped + 5) / 10;
  if (normalized < 0.5) {
    const t = normalized * 2;
    return `rgb(${Math.round(220 - t * 60)}, ${Math.round(50 + t * 130)}, ${Math.round(50 + t * 130)})`;
  }
  const t = (normalized - 0.5) * 2;
  return `rgb(${Math.round(160 - t * 140)}, ${Math.round(180 + t * 60)}, ${Math.round(180 - t * 100)})`;
}

function getTextColor(momentum) {
  if (momentum == null) return '#1f2937';
  return Math.abs(momentum) > 2 ? '#fff' : '#1f2937';
}

function formatModelName(name) {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function buildSectorGroups(sectorDetails, timeframe) {
  if (!sectorDetails?.length) return [];
  return sectorDetails.map(sector => ({
    groupName: sector.sector,
    groupMomentum: sector[`${timeframe}_momentum`],
    items: (sector.industries || []).map(ind => ({
      name: ind.industry,
      size: ind.count || 1,
      momentum: ind[`${timeframe}_momentum`] ?? 0,
      daily_momentum: ind.daily_momentum,
      weekly_momentum: ind.weekly_momentum,
      monthly_momentum: ind.monthly_momentum,
      quarterly_momentum: ind.quarterly_momentum,
      count: ind.count,
    })),
  })).filter(g => g.items.length > 0);
}

function buildBusinessModelGroups(categories, timeframe) {
  if (!categories?.length) return [];
  return categories.map(cat => {
    const subs = cat.subcategories || [];
    if (subs.length === 0) {
      return {
        groupName: formatModelName(cat.category),
        groupMomentum: cat[`${timeframe}_momentum`],
        items: [{
          name: formatModelName(cat.category),
          size: cat.company_count || 1,
          momentum: cat[`${timeframe}_momentum`] ?? 0,
          daily_momentum: cat.daily_momentum,
          weekly_momentum: cat.weekly_momentum,
          monthly_momentum: cat.monthly_momentum,
          quarterly_momentum: cat.quarterly_momentum,
          count: cat.company_count,
        }],
      };
    }
    return {
      groupName: formatModelName(cat.category),
      groupMomentum: cat[`${timeframe}_momentum`],
      items: subs.map(sub => ({
        name: formatModelName(sub.subcategory),
        size: sub.company_count || 1,
        momentum: sub[`${timeframe}_momentum`] ?? 0,
        daily_momentum: sub.daily_momentum,
        weekly_momentum: sub.weekly_momentum,
        monthly_momentum: sub.monthly_momentum,
        quarterly_momentum: sub.quarterly_momentum,
        count: sub.company_count,
      })),
    };
  }).filter(g => g.items.length > 0);
}

const HeatmapCell = ({ item, totalSize, onHover, onLeave }) => {
  const color = getMomentumColor(item.momentum);
  const textColor = getTextColor(item.momentum);
  // flex-grow proportional to size so bigger industries get more space
  const flexGrow = item.size;

  return (
    <div
      className="heatmap-cell"
      style={{ backgroundColor: color, flexGrow, flexBasis: 0 }}
      onMouseEnter={() => onHover(item)}
      onMouseLeave={onLeave}
    >
      <span className="heatmap-cell__name" style={{ color: textColor }}>{item.name}</span>
      <span className="heatmap-cell__value" style={{ color: textColor }}>
        {item.momentum >= 0 ? '+' : ''}{item.momentum.toFixed(1)}%
      </span>
    </div>
  );
};

const HeatmapTooltip = ({ item }) => {
  if (!item) return null;
  return (
    <div className="heatmap-tooltip-fixed">
      <div className="heatmap-tooltip__name">{item.name}</div>
      {item.count != null && (
        <div className="heatmap-tooltip__count">{item.count} companies</div>
      )}
      <div className="heatmap-tooltip__metrics">
        {[
          ['Today', item.daily_momentum],
          ['Week', item.weekly_momentum],
          ['Month', item.monthly_momentum],
          ['Quarter', item.quarterly_momentum],
        ].map(([label, val]) => val != null && (
          <div key={label} className="heatmap-tooltip__row">
            <span>{label}</span>
            <span className={val >= 0 ? 'positive' : 'negative'}>
              {formatPercentage(val)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketHeatmap = ({ sectorDetails, businessModelCategories }) => {
  const [viewMode, setViewMode] = useState('sectors');
  const [timeframe, setTimeframe] = useState('daily');
  const [hoveredItem, setHoveredItem] = useState(null);

  const groups = useMemo(() => {
    if (viewMode === 'sectors') {
      return buildSectorGroups(sectorDetails, timeframe);
    }
    return buildBusinessModelGroups(businessModelCategories, timeframe);
  }, [viewMode, timeframe, sectorDetails, businessModelCategories]);

  // Calculate total size across all groups for proportional row heights
  const totalSize = useMemo(() =>
    groups.reduce((sum, g) => sum + g.items.reduce((s, i) => s + i.size, 0), 0),
    [groups]
  );

  if (!sectorDetails?.length && !businessModelCategories?.length) {
    return <div className="heatmap-empty">No heatmap data available</div>;
  }

  return (
    <div className="market-heatmap">
      <div className="heatmap-controls">
        <div className="heatmap-pill-group">
          {VIEW_MODES.map(mode => (
            <button
              key={mode.key}
              className={`heatmap-pill ${viewMode === mode.key ? 'heatmap-pill--active' : ''}`}
              onClick={() => setViewMode(mode.key)}
            >
              {mode.label}
            </button>
          ))}
        </div>
        {!CHART_VIEWS[viewMode] && (
          <div className="heatmap-pill-group">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf.key}
                className={`heatmap-pill ${timeframe === tf.key ? 'heatmap-pill--active' : ''}`}
                onClick={() => setTimeframe(tf.key)}
              >
                {tf.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {CHART_VIEWS[viewMode] || (
        <>
          <div className="heatmap-grid-container">
            <div className="heatmap-grid">
              {groups.map(group => {
                const groupSize = group.items.reduce((s, i) => s + i.size, 0);
                const heightPct = Math.max((groupSize / totalSize) * 100, 5);
                return (
                  <div
                    key={group.groupName}
                    className="heatmap-group"
                    style={{ height: `${heightPct}%` }}
                  >
                    <div className="heatmap-group__label">{group.groupName}</div>
                    <div className="heatmap-group__cells">
                      {group.items.map(item => (
                        <HeatmapCell
                          key={item.name}
                          item={item}
                          totalSize={groupSize}
                          onHover={setHoveredItem}
                          onLeave={() => setHoveredItem(null)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <HeatmapTooltip item={hoveredItem} />
          </div>

          <div className="heatmap-legend">
            <span className="heatmap-legend__label">Strong Decline</span>
            <div className="heatmap-legend__bar" />
            <span className="heatmap-legend__label">Strong Rally</span>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketHeatmap;
